/** Error state component for pages and sections that fail to load data. */
'use client';

import { AlertCircle } from 'lucide-react';
import { clsx } from 'clsx';
import Button from './Button';

/**
 * Props for the ErrorState component.
 */
export interface ErrorStateProps {
  /** Error message to display */
  message?: string;
  /** Callback to retry the failed operation */
  onRetry?: () => void;
  /** A way out when retrying can't help (e.g. a record that no longer exists). */
  action?: { label: string; onClick: () => void };
  /** Additional CSS classes */
  className?: string;
  /** Automation handle: the root gets it, the buttons `${testId}-retry` / `${testId}-action`. */
  testId?: string;
}

/**
 * Displays an error message with an icon, an optional retry button and an
 * optional way out.
 * Use as a replacement for loading spinners when data fetching fails.
 * @param props - Error message, retry callback, and optional className.
 * @returns An accessible error alert with retry action.
 */
export default function ErrorState({
  message = 'Something went wrong. Please try again.',
  onRetry,
  action,
  className,
  testId,
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      data-testid={testId}
      className={clsx(
        'flex flex-col items-center justify-center h-64 gap-4',
        className
      )}
    >
      <div className="flex items-center gap-2 text-red-600 px-4 text-center">
        <AlertCircle className="h-6 w-6 shrink-0" />
        <p className="text-body font-medium">{message}</p>
      </div>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry} data-testid={testId ? `${testId}-retry` : undefined}>
          Try again
        </Button>
      )}
      {action && (
        <Button variant="outline" size="sm" onClick={action.onClick} data-testid={testId ? `${testId}-action` : undefined}>
          {action.label}
        </Button>
      )}
    </div>
  );
}
